import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {AntragTicketSchoolInfoDto} from "../../../../models/antrag-ticket/AntragTicketSchoolInfoDto";
import {Observable, of, Subject, throwError} from "rxjs";
import {TextbausteinService} from "../../../../@core/services/textbaustein/textbaustein.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {FieldConfigService} from "../../../../@core/services/field-config/field-config.service";
import {
  AntragTicketSchoolInfoService
} from "../../../../@core/services/antrag-ticket/antrag-ticket-school-info.service";
import {CustomToastService} from "../../../../@core/utils/custom-toast.service";
import {pairwise, startWith, tap} from "rxjs/operators";
import {KeyString} from "../../../../models/textbaustein/KeyString";
import {SchoolType} from "../../../../models/schule/SchoolType";
import {PFLICHTFELDMARKER} from "../../../../../assets/constants/constants";
import {SchuleDTO} from "../../../../models/schule/SchuleDTO";
import {Wegtyp} from "../../../../models/antrag-ticket/Wegtyp";
import {AntragTicketSchoolInfoUpdateDto} from "../../../../models/antrag-ticket/AntragTicketSchoolInfoUpdateDto";
import {JsonHelper} from "../../../../@core/utils/json-helper";
import {SchuljahrDTO} from "../../../../models/schuljahr/SchuljahrDTO";
import {KlasseDTO} from "../../../../models/klasse/KlasseDTO";
import {BildungsgangDTO} from "../../../../models/bildungsgang/BildungsgangDTO";
import {SelectComponent} from "../../../../shared/form/select/select.component";
import {InputComponent} from "../../../../shared/form/input/input.component";
import {DatePipe} from "@angular/common";

@Component({
  selector: 'app-school-info',
  imports: [
    TranslatePipe,
    SelectComponent,
    InputComponent,
    DatePipe,
    ReactiveFormsModule,
    TranslatePipe,
    TranslatePipe
  ],
  templateUrl: './school-info.component.html',
  styleUrl: './school-info.component.scss'
})
export class SchoolInfoComponent implements OnInit, OnChanges {
  @Input() antragId;
  @Input() existsSessionStorageTextbausteine;
  @Input() effectivelyVisible;
  @Input() effectiveValidators;
  @Input() fieldConfig;
  @Input() isEntwurf;
  @Input() wegtyp;
  @Input() minDate;
  @Input() schoolList;
  @Input() schoolYearList;
  @Input() classList;
  @Input() courseList;
  @Input() schoolLevelList;
  @Input() schoolDaysAWeek;
  @Input() courseDate;

  @Output() schoolChangeEvent = new EventEmitter<any>();
  @Output() schoolYearChangeEvent = new EventEmitter<any>();

  form: UntypedFormGroup

  schoolInfo: AntragTicketSchoolInfoDto;

  showSchoolLevel: boolean = false;

  optionSchoolLevel: {translation: string, name: string}[];

  optionSchoolType;

  updateSchoolInfoLoading: boolean = false;

  waitForSchoolInfoUpdate$ = new Subject<void>();

  constructor(
    protected readonly fb: UntypedFormBuilder,
    protected readonly textbausteinService: TextbausteinService,
    protected readonly translateService: TranslateService,
    protected readonly fieldConfigService: FieldConfigService,
    protected readonly antragTicketSchoolInfoService: AntragTicketSchoolInfoService,
    protected readonly customToastService: CustomToastService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.antragId) {
      this.getSchoolInfo();
      this.setValidators();
    }
    if (changes.classList || changes.courseList) {
      this.updateSchoolLevelVisibility();
    }
    if (changes.schoolDaysAWeek) {
      this.getFormItem('schoolDaysAWeek')?.patchValue(changes.schoolDaysAWeek.currentValue);
    }
    if (changes.courseDate) {
      this.getFormItem('courseDate')?.patchValue(changes.courseDate.currentValue);
    }
    if (changes.schoolLevelList) {
      this.loadOptionSchoolLevel();
    }
  }

  ngOnInit() {
    this.form = this.fb.group({
      schoolId: [''],
      schoolYearId: [''],
      classId: [''],
      courseId: [''],
      schoolLevel: [''],
      schoolType: [''],
      schoolDaysAWeek: [this.schoolDaysAWeek],
      courseDate: [this.courseDate]
    });

    this.createChangeEvents();

    this.getSchoolInfo();

    this.optionSchoolType = [{id: null, name: "-" },
      {id:"ELEMENTARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.ELEMENTARY") },
      {id:"GENERAL_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GENERAL_SECONDARY") },
      {id:"INTERMEDIATE_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.INTERMEDIATE_SECONDARY") },
      {id:"GRAMMAR", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GRAMMAR") },
      {id:"COMPREHENSIVE", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.COMPREHENSIVE") },
      {id:"VOCATIONAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.VOCATIONAL") },
      {id:"SPECIAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.SPECIAL") },
      {id:"OTHER", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.OTHER") }
    ];

    this.loadOptionSchoolLevel();

    this.updateSchoolLevelVisibility();
    this.setValidators();
  }

  loadOptionSchoolLevel() {
    this.optionSchoolLevel = [
      {translation: 'EMPTY', name: "" }
    ];

    for (let schoolLevel of this.schoolLevelList) {
      this.optionSchoolLevel.push(
        {translation: schoolLevel, name: schoolLevel },
      )
    }
  }

  updateSchoolLevelVisibility() {
    this.showSchoolLevel = this.classList.length == 0 && this.courseList.length == 0 && this.getFormItem('schoolId')?.value;
  }

  createChangeEvents() {
    this.getFormItem('schoolId')?.valueChanges
      .pipe(startWith(null),pairwise())
      .subscribe(([prev, next]: [any, any]) => {
        if (next!==prev) {
          this.resetFields();
          this.schoolChange();
          const school = this.schoolList.find(s => s.id === this.getFormItem('schoolId')?.value);
          this.getFormItem('schoolType')?.patchValue(school ? school.schoolType : null);
          this.setValidators();
        }
      });

    this.getFormItem('schoolYearId')?.valueChanges
      .pipe(startWith(null),pairwise())
      .subscribe(([prev, next]: [any, any]) => {
        if (next!==prev) {
          this.schoolYearChangeEvent.emit(this.getFormItem('schoolYearId')?.value);
          this.setValidators();
        }
      });
  }

  getSchoolInfo() {
    if (!this.antragId) {
      return;
    }
    if (this.schoolList == null || this.schoolYearList == null || this.classList == null || this.courseList == null) {
      console.log('Cannot get school info: required lists are missing');
      return;
    }
    this.antragTicketSchoolInfoService.getSchoolInfoByAntrag(this.antragId)
      .subscribe(schoolInfo => {
        this.schoolInfo = schoolInfo;
        this.updateForm();
      });
  }

  updateForm() {
    let schoolId = this.schoolInfo?.schoolRef?.schoolId;
    let schoolYearId = this.schoolInfo?.schoolYearRef?.schoolYearId;
    let classId = this.schoolInfo?.classRef?.classId;
    let courseId = this.schoolInfo?.courseRef?.courseId;
    this.form.patchValue({
      schoolId: this.mapSchoolUUIDToId(schoolId) || '',
      schoolYearId: this.mapSchoolYearUUIDToId(schoolYearId) || '',
      classId: this.mapClassUUIDToId(classId) || '',
      courseId: this.mapCourseUUIDToId(courseId) || '',
      schoolLevel: this.schoolInfo?.schoolLevel || '',
      schoolType: this.schoolInfo?.schoolType || '',
      schoolDaysAWeek: this.schoolDaysAWeek || '',
      courseDate: this.courseDate || ''
    });
  }

  getTextbausteinOrTranslate(textbausteinKey: KeyString, translateKey: string): string {
    return this.existsSessionStorageTextbausteine?
      this.textbausteinService.printTextbausteinByKey(textbausteinKey, translateKey?
        this.translateService.instant(translateKey) : '') : '';
  }

  getFieldLabelOrTranslate(fieldName: string, translateKey: string): string {
    return ''+(this.isRequiredByConfig(fieldName)? PFLICHTFELDMARKER : '')
      + this.fieldConfigService.getFieldLabel(fieldName, this.fieldConfig, translateKey? this.translateService.instant(translateKey) : '');
  }

  isRequiredByConfig(fieldName: string) : boolean {
    return this.fieldConfigService.getFieldFromFieldConfig(fieldName, this.fieldConfig)?.required === true;
  }

  getFormItem(s: string) : AbstractControl {
    return this.form?.get(s);
  }

  isBerufskolleg(id): boolean {
    if (!id) {
      return false;
    }
    return this.schoolList?.find((schule: SchuleDTO) => schule.id === id)?.schoolType === SchoolType.VOCATIONAL;
  }

  setValidators() {
    this.effectiveValidators.forEach((varName)=>{
      this.getFormItem(varName)?.setValidators([Validators.required]);
      if (this.isEntwurf && varName === 'schuleId') {
        this.getFormItem('schoolId')?.setValidators([Validators.required]);
      } else if (this.isEntwurf && varName === 'schuljahrId'
        && [Wegtyp.SCHULWEG,Wegtyp.BEIDES].includes(this.wegtyp)
      ) {
        this.getFormItem('schoolYearId')?.setValidators([Validators.required]);
      } else if (
        this.isEntwurf && varName === 'klasseId'
        && [Wegtyp.SCHULWEG,Wegtyp.BEIDES].includes(this.wegtyp)
        && this.getFormItem('schoolId')?.value
        && !this.isBerufskolleg(this.getFormItem('schoolId')?.value)
      ) {
        this.getFormItem('classId')?.setValidators([Validators.required]);
      } else if (
        this.isEntwurf && varName === 'bildungsgangId'
        && [Wegtyp.SCHULWEG,Wegtyp.BEIDES].includes(this.wegtyp)
        && this.getFormItem('schoolId')?.value
        && this.isBerufskolleg(this.getFormItem('schoolId')?.value)
      ) {
        this.getFormItem('courseId')?.setValidators([Validators.required]); // clear bildungsgang validators
      } else {
        this.getFormItem('courseId')?.clearValidators();
      }
    });
  }

  updateSchoolInfo(): Observable<AntragTicketSchoolInfoDto> {
    this.updateSchoolInfoLoading = true;
    if (!this.antragId) {
      console.log('Cannot update school info: antragId is missing');
      this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.ANTRAG_ID_MISSING'));
      this.updateSchoolInfoLoading = false;
      return throwError(() => new Error('Cannot update school info: antragId is missing'));
    }
    if (this.form.invalid) {
      this.updateSchoolInfoLoading = false;
      this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_ERROR_FORM_INVALID'));
      return throwError(() => new Error('School info form is invalid'));
    }
    if (this.form.pristine) {
      this.updateSchoolInfoLoading = false;
      return of(this.schoolInfo);
    }
    const updateDto: AntragTicketSchoolInfoUpdateDto = {
      schoolId: this.mapSchoolIdToUUID(this.getFormItem('schoolId')?.value),
      schoolYearId: this.mapSchoolYearIdToUUID(this.getFormItem('schoolYearId')?.value),
      classId: this.mapClassIdToUUID(this.getFormItem('classId')?.value),
      courseId: this.mapCourseIdToUUID(this.getFormItem('courseId')?.value),
      schoolDaysAWeek: this.getFormItem('schoolDaysAWeek')?.value,
      courseDate: this.getFormItem('courseDate')?.value,
      schoolLevel: this.getFormItem('schoolLevel')?.value
    }
    return this.antragTicketSchoolInfoService.updateSchoolInfoByAntrag(this.antragId, updateDto)
      .pipe(
        tap({
          next: schoolInfo => {
            this.schoolInfo = schoolInfo;
            this.schoolDaysAWeek = updateDto.schoolDaysAWeek;
            this.courseDate = updateDto.courseDate;
            this.updateForm();
            this.customToastService.showSuccess(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_SUCCESS'));
            this.updateSchoolInfoLoading = false;
          },
          error: err => {
            const error = JsonHelper.parseToJson(err.error)
            this.customToastService.showError(this.translateService.instant('TICKETDETAIL.SCHOOL_INFO.UPDATE_ERROR', {'errorCode': error?.reason || 'unknown'}));
            this.updateSchoolInfoLoading = false;
          },
          complete: () => {
            this.waitForSchoolInfoUpdate$.next();
            this.waitForSchoolInfoUpdate$.complete();
            this.waitForSchoolInfoUpdate$.unsubscribe();
            this.waitForSchoolInfoUpdate$ = new Subject<void>();
            this.form.markAsPristine();
          }
        })
      );
  }

  /**
   * Wrapper method for template calls to updateSchoolInfo.
   * Subscribes to the observable automatically.
   */
  onUpdateSchoolInfoClick(): void {
    this.updateSchoolInfo().subscribe({
      error: (err) => {
        // Error already handled in the tap operator
        console.error('School info update failed:', err);
      }
    });
  }

  mapSchoolIdToUUID(schoolId): string {
    const school: SchuleDTO = this.schoolList.find(s => s.id === schoolId);
    return school ? school.schuleId : null;
  }

  mapSchoolUUIDToId(schoolUUID): number {
    const school: SchuleDTO = this.schoolList.find(s => s.schuleId === schoolUUID);
    return school ? school.id : null;
  }

  mapSchoolYearIdToUUID(schoolYearId): string {
    const schoolYear: SchuljahrDTO = this.schoolYearList.find(sy => sy.id === schoolYearId);
    return schoolYear ? schoolYear.schuljahrId : null;
  }

  mapSchoolYearUUIDToId(schoolYearUUID): number {
    const schoolYear: SchuljahrDTO = this.schoolYearList.find(sy => sy.schuljahrId === schoolYearUUID);
    return schoolYear ? schoolYear.id : null;
  }

  mapClassIdToUUID(classId): string {
    const classObj: KlasseDTO = this.classList.find(c => c.id === classId);
    return classObj ? classObj.klasseId : null;
  }

  mapClassUUIDToId(classUUID): number {
    const classObj: KlasseDTO = this.classList.find(c => c.klasseId === classUUID);
    return classObj ? classObj.id : null;
  }

  mapCourseIdToUUID(courseId): string {
    const course: BildungsgangDTO = this.courseList.find(c => c.id === courseId);
    return course ? course.bildungsgangId : null;
  }

  mapCourseUUIDToId(courseUUID): number {
    const course: BildungsgangDTO = this.courseList.find(c => c.bildungsgangId === courseUUID);
    return course ? course.id : null;
  }

  schoolChange() {
    this.schoolChangeEvent.emit(this.getFormItem('schoolId')?.value);
  }

  getSchoolId() {
    return this.getFormItem('schoolId')?.value;
  }

  getSchoolYearId() {
    return this.getFormItem('schoolYearId')?.value;
  }

  resetFields() {
    this.form.patchValue({
      schoolType: null,
      classId: null,
      courseId: null,
      schoolYearId: null,
      schoolLevel: null,
      courseDate: null
    });
  }

  protected readonly KeyString = KeyString;
}
